import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor from "#root/components/standard-editor";
import { WithSidebar } from "#root/components/withSidebar";
import { accountCollection } from "#root/schema/account";
import { vendorCollection, type VendorListItem } from "#root/schema/vendor";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function ({ id }): ReactNode {
  const navigate = useNavigate();
  const [vendors, setVendors] = useData(async () => {
    let { rows } = await vendorCollection.list();
    rows = rows.sort(({ name = "" }, { name: name2 = "" }) => (name ?? "").localeCompare(name2 ?? ""));
    return rows;
  }, []);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<VendorListItem>) => {
      setVendors(mergeUpdate(vendors!, data));

      if ("deleted" in data) {
        navigate(`/admin/vendor/${data.deleted}`);
      } else if ("created" in data) {
        navigate(`/admin/vendor/${data.created}`);
      }
    },
    [navigate, setVendors, vendors],
  );

  if (!vendors) {
    return (
      <PageLayout title="Vendor">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Vendor">
      <WithSidebar>
        <VendorSidebar vendors={vendors} id={id}/>
        <section>
          <Switch>
            <Case expr={id === "new"}>
              <h1>New Vendor</h1>
              <VendorEditor collection={vendorCollection} onSave={handleUpdate}/>
            </Case>
            <Case expr={id != null}>
              <h1>Edit Vendor</h1>
              <VendorEditor id={id} collection={vendorCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
            </Case>
          </Switch>
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function VendorSidebar({ vendors, id }) {
  const navigate = useNavigate();
  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate("/admin/vendor/new")}>
        New Vendor
      </button>
      <Accordion.Parent initialItemId={id} initialGroupId="By Name">
        <Accordion.Group label="By Name">
          {vendors.map(({ id, name }) => (
            <Accordion.Item key={id} itemId={id}>
              <Link to={"/admin/vendor/" + id}>{name}</Link>
            </Accordion.Item>
          ))}
        </Accordion.Group>
      </Accordion.Parent>
    </Sidebar>
  );
}

function VendorEditor(props) {
  return (
    <StandardEditor {...props} name="Vendor">
      <Fields.Set name="accountId">
        <label>Account</label>
        <Fields.AutoComplete required collection={accountCollection}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="name">
        <label>Vendor Name</label>
        <Fields.CompanyName required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="website">
        <label>Website</label>
        <Fields.Domain/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="image">
        <label>Image URI</label>
        <Fields.TextArea/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="group">
        <label>Listing Group</label>
        <Fields.Select>
          <Fields.Option value="none">Not Listed</Fields.Option>
          <Fields.Option value="dataSource">Data Source</Fields.Option>
          <Fields.Option value="newsSource">News Source</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="profile">
        <label>Profile (Markdown)</label>
        <Fields.Markdown/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
